import { api } from "./api";
import { evidenceBaseApi } from "./evidenceBaseApi";

export interface MasterItem {
  id: string;
  name: string;
  is_active: boolean;
}

export interface NetsuiteClientItem {
  id: string;
  netsuite_id: string;
  entity_id: string | null;
  name: string;
  is_active: boolean;
}

export interface KantataProjectItem {
  id: string;
  kantata_id: string;
  code: string | null;
  name: string;
  is_active: boolean;
  client_names: string[] | null;
}

export interface ApiConfigurationItem {
  id: string;
  name: string;
  description: string | null;
  configuration: {
    api_url: string;
    queue_name: string | null;
  };
  template_type_id: number | null;
  template_formattype_ids: number[] | null;
  is_active: boolean;
  // Computed properties from backend
  api_url?: string;
  queue_name?: string | null;
}

export const masterDataApi = {
  // Read (for dropdowns — active only)
  therapyAreas: (): Promise<MasterItem[]> => api.get("/api/master/therapy-areas"),
  netsuiteClients: (search: string): Promise<NetsuiteClientItem[]> =>
    api.get(`/api/master/netsuite-clients?search=${encodeURIComponent(search)}&limit=50`),
  netsuiteClientById: (id: string): Promise<NetsuiteClientItem> =>
    api.get(`/api/master/netsuite-clients/${id}`),
  kantataProjects: (search: string, limit: number = 50): Promise<KantataProjectItem[]> =>
    api.get(`/api/master/kantata-projects?search=${encodeURIComponent(search)}&limit=${limit}`),
  kantataProjectById: (id: string): Promise<KantataProjectItem> =>
    api.get(`/api/master/kantata-projects/${id}`),
  kantataProjectClients: (kantataProjectId: string): Promise<NetsuiteClientItem[]> =>
    api.get(`/api/master/kantata-projects/${kantataProjectId}/clients`),
  evidenceTypes: (): Promise<MasterItem[]> => evidenceBaseApi.evidenceTypes(),
  sourceTypes: (): Promise<MasterItem[]> => evidenceBaseApi.sourceTypes(),
  topics: (): Promise<MasterItem[]> => evidenceBaseApi.topics(),

  // Admin list (all records including inactive)
  adminTherapyAreas: (): Promise<MasterItem[]> => api.get("/api/admin/therapy-areas"),

  // Admin CRUD — therapy areas
  createTherapyArea: (name: string): Promise<MasterItem> =>
    api.post("/api/admin/therapy-areas", { name }),
  updateTherapyArea: (id: string, data: Partial<{ name: string; is_active: boolean }>): Promise<MasterItem> =>
    api.put(`/api/admin/therapy-areas/${id}`, data),
  deleteTherapyArea: (id: string): Promise<void> =>
    api.delete(`/api/admin/therapy-areas/${id}`),

  // Admin CRUD — evidence types (uses Evidence Base Service)
  createEvidenceType: (name: string): Promise<MasterItem> =>
    evidenceBaseApi.createEvidenceType({ id: crypto.randomUUID(), name, is_active: true }),
  updateEvidenceType: (id: string, data: Partial<{ name: string; is_active: boolean }>): Promise<MasterItem> =>
    evidenceBaseApi.updateEvidenceType(id, data),
  deleteEvidenceType: (id: string): Promise<void> =>
    evidenceBaseApi.deleteEvidenceType(id),

  // Admin CRUD — source types (uses Evidence Base Service)
  createSourceType: (name: string): Promise<MasterItem> =>
    evidenceBaseApi.createSourceType({ id: crypto.randomUUID(), name, is_active: true }),
  updateSourceType: (id: string, data: Partial<{ name: string; is_active: boolean }>): Promise<MasterItem> =>
    evidenceBaseApi.updateSourceType(id, data),
  deleteSourceType: (id: string): Promise<void> =>
    evidenceBaseApi.deleteSourceType(id),

  // Admin CRUD — api configurations
  adminApiConfigurations: (): Promise<ApiConfigurationItem[]> =>
    api.get("/api/admin/api-configurations"),
  createApiConfiguration: (data: Omit<ApiConfigurationItem, "id">): Promise<ApiConfigurationItem> =>
    api.post("/api/admin/api-configurations", data),
  updateApiConfiguration: (id: string, data: Partial<Omit<ApiConfigurationItem, "id">>): Promise<ApiConfigurationItem> =>
    api.put(`/api/admin/api-configurations/${id}`, data),
  deleteApiConfiguration: (id: string): Promise<void> =>
    api.delete(`/api/admin/api-configurations/${id}`),
};
